import { isUnknownArray } from "../types/data";
import { isDemoUserId, markDemoActivity } from "./demoSession";
import type { ActivityRecord } from "../types/activity";

const DEMO_SANDBOX_PREFIX = "cineparagon_demo_sandbox_v1";

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const getStorageKey = (scope: string, userId: string|null|undefined, instanceId = "") => {
  if (!isDemoUserId(userId)) return "";
  const normalizedScope = String(scope || "").trim();
  if (!normalizedScope) return "";
  return [
    DEMO_SANDBOX_PREFIX,
    encodeURIComponent(String(userId)),
    encodeURIComponent(normalizedScope),
    encodeURIComponent(String(instanceId || "current"))
  ].join(":");
};

export const readDemoSandboxState = <T = unknown>(scope: string, userId: string, instanceId = ""): T | null => {
  if (!canUseStorage()) return null;
  const key = getStorageKey(scope, userId, instanceId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeDemoSandboxState = <T>(
  scope: string,
  userId: string,
  instanceId = "",
  value: T
) => {
  if (!canUseStorage()) return value;
  const key = getStorageKey(scope, userId, instanceId);
  if (!key) return value;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    markDemoActivity(userId);
  } catch {}
  return value;
};

export const patchDemoSandboxState = <T extends object>(
  scope: string,
  userId: string,
  instanceId = "",
  patch: Partial<T> = {}
) => {
  const current = readDemoSandboxState<Partial<T>>(scope, userId, instanceId) || {};
  return writeDemoSandboxState(scope, userId, instanceId, {
    ...current,
    ...(patch || {})
  });
};

interface DemoActivity extends ActivityRecord { dedupeKey?: string; }
const getDemoActivityState = (userId: string) =>
  readDemoSandboxState<{ events: DemoActivity[] }>("activities", userId) || { events: [] };

export const hasDemoActivity = (userId: string, dedupeKey: string) => {
  if (!dedupeKey) return false;
  return (getDemoActivityState(userId).events || [])
    .some((event) => event.dedupeKey === dedupeKey);
};

export const logDemoActivity = (userId: string | null | undefined, activity: ActivityRecord, dedupeKey = "") => {
  if (!isDemoUserId(userId)) return false;
  const state = getDemoActivityState(userId);
  const events = isUnknownArray(state.events) ? state.events : [];
  if (dedupeKey && events.some((event) => event.dedupeKey === dedupeKey)) {
    return false;
  }
  writeDemoSandboxState("activities", userId, "", {
    events: [
      {
        ...activity,
        dedupeKey,
        timestamp: new Date().toISOString()
      },
      ...events
    ].slice(0, 120)
  });
  return true;
};

export const removeDemoActivity = (userId: string, dedupeKey: string) => {
  if (!isDemoUserId(userId) || !dedupeKey) return false;
  const state = getDemoActivityState(userId);
  const events = isUnknownArray(state.events) ? state.events : [];
  const nextEvents = events.filter((event) => event.dedupeKey !== dedupeKey);
  if (nextEvents.length === events.length) return false;
  writeDemoSandboxState("activities", userId, "", { events: nextEvents });
  return true;
};
